import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { SoftDecodedText } from './SoftDecodedText'
import { advanceNativeSmoothDrain, nativeSmoothDrainStep, segmentNativeGraphemes, visibleNativeSmoothCount } from '../lib/nativeSmoothDrain'

interface Props {
  text: string
  active: boolean
  onVisibleChange?: () => void
}

export function SmoothDecodedText({ text, active, onVisibleChange }: Props) {
  const targetGraphemes = useMemo(() => segmentNativeGraphemes(text), [text])
  const [displayed, setDisplayed] = useState(() => active ? 0 : targetGraphemes.length)
  const previousTarget = useRef(text)
  const targetCount = useRef(targetGraphemes.length)
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true
  const appendOnly = text.startsWith(previousTarget.current)
  const drainActive = active && appendOnly && !reducedMotion
  const visibleCount = visibleNativeSmoothCount(displayed, targetGraphemes.length, drainActive)
  const visibleText = drainActive ? targetGraphemes.slice(0, visibleCount).join('') : text
  const needsDrain = drainActive && displayed < targetGraphemes.length

  useLayoutEffect(() => {
    targetCount.current = targetGraphemes.length
    previousTarget.current = text

    if (!drainActive) {
      if (displayed !== targetGraphemes.length) setDisplayed(targetGraphemes.length)
      return
    }
    if (displayed > targetGraphemes.length) setDisplayed(targetGraphemes.length)
  }, [displayed, drainActive, targetGraphemes.length, text])

  // Deliberately do not key this timer to `text`: incoming SSE chunks may extend
  // targetCount while a tick is pending, but they must not postpone that tick.
  useEffect(() => {
    if (!needsDrain) return

    const backlog = targetGraphemes.length - displayed
    const { delayMs } = nativeSmoothDrainStep(backlog)
    const timer = window.setTimeout(() => {
      setDisplayed((current) => advanceNativeSmoothDrain(current, targetCount.current))
    }, delayMs)
    return () => window.clearTimeout(timer)
  }, [displayed, needsDrain])

  useLayoutEffect(() => {
    onVisibleChange?.()
  }, [onVisibleChange, visibleText])

  return <SoftDecodedText text={visibleText} active={active} />
}
